'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  type CalculatorConfig,
  type ControlValues,
  type NumberControl,
  type CalculatorControl,
  DataTableInputMode
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { PoissonCalculator, type PoissonResult } from './poisson-calculator';
import { calculatePoissonDistribution } from '../../../lib/stats';

export interface PoissonControlValues extends ControlValues {
  lambda: number;
  events: number;
}

export const poissonControls = {
  tabs: [
    {
      title: 'Parameters',
      controls: [
        {
          id: 'lambda',
          type: ControlType.Number,
          label: 'Lambda (average rate)',
          required: true,
          min: 0.1,
          max: 100,
          step: 0.1,
          defaultValue: 5
        } satisfies NumberControl,
        {
          id: 'events',
          type: ControlType.Number,
          label: 'Number of Events (k)',
          required: true,
          min: 0,
          max: 100,
          step: 1,
          defaultValue: 3
        } satisfies NumberControl
      ]
    }
  ]
} satisfies { tabs: { title: string; controls: readonly CalculatorControl[] }[] };

export const poissonConfig: CalculatorConfig<CalculatorId.POISSON_DISTRIBUTION> = {
  id: CalculatorId.POISSON_DISTRIBUTION,
  type: CalculatorType.Graph,
  name: 'Poisson Distribution Calculator',
  dataTableInputMode: DataTableInputMode.None,
  initialData: TableDataJSON.from({ columns: [] }), // No data needed for this calculator
  initialControlValues: {
    lambda: 5,
    events: 3
  },
  controls: poissonControls,
  getInitialConfig: () => ({
    lambda: 5,
    events: 3
  }),
  getVisualizationData: ({ controls }) => {
    const poissonControls = controls as PoissonControlValues;
    const lambda = Number(poissonControls.lambda);
    const events = Number(poissonControls.events);

    // Validate inputs
    if (
      isNaN(lambda) ||
      isNaN(events) ||
      lambda <= 0 ||
      events < 0
    ) {
      return null;
    }

    try {
      // Calculate poisson distribution
      const { pdf, cdf } = calculatePoissonDistribution({
        events: Math.floor(events), // Ensure events is an integer
        lambda
      });

      // Generate full distribution for visualization
      const distribution: number[] = [];
      const eventsArray: number[] = [];
      const maxEvents = Math.min(Math.ceil(lambda * 3), 50); // Show up to 3*lambda or 50 events, whichever is smaller
      
      for (let k = 0; k <= maxEvents; k++) {
        try {
          const { pdf } = calculatePoissonDistribution({
            events: k,
            lambda
          });
          distribution.push(pdf);
          eventsArray.push(k);
        } catch (error) {
          console.error(`Error calculating PDF for k=${k}:`, error);
          distribution.push(0);
          eventsArray.push(k);
        }
      }

      // Ensure we have a reasonable number of data points for better visualization
      // Poisson distributions are discrete, so we need integer k values
      if (distribution.length < 5) {
        // If we have too few points, add some more
        const additionalPoints = Math.max(0, 5 - distribution.length);
        for (let i = 0; i < additionalPoints; i++) {
          const k = maxEvents + i + 1;
          eventsArray.push(k);
          distribution.push(0); // Very low probability for these higher values
        }
      }

      const result: PoissonResult = {
        pdf,
        cdf,
        distribution,
        events: eventsArray,
        currentK: Math.floor(events),
        lambda: lambda
      };
      
      return result;
    } catch (error) {
      console.error('Error calculating Poisson distribution:', error);
      return null;
    }
  },
  render: ({ visualizationData }) => <PoissonCalculator visualizationData={visualizationData} />
}; 